import React from 'react';
import { RouteObject } from 'react-router-dom';

// Static imports — these are NOT lazy-loaded because they are either:
// - Used as Suspense/error boundary wrappers (VXErrorBoundary)
// - The Suspense fallback itself (LoadingPage)
// - Inline utility components rendered alongside lazy pages (TranscriptDisplay)
import VXErrorBoundary from 'src/components/visionx/error-boundary/VXErrorBoundary';
import TranscriptDisplay from 'src/components/common/vx/table/speech-to-text/TranscriptDisplay';
import LoadingPage from 'src/pages/visionx/LoadingPage';

// ---------------------------------------------------------------------------
// Lazy page-level components
// All components that are the direct `element` value of a RouteObject are
// loaded dynamically so Vite can split them into separate chunks.
// ---------------------------------------------------------------------------

// Pre-auth pages
const NotFound = React.lazy(() =>
  import('src/pages/common/NotFound').then((m) => ({ default: m.NotFound })),
);
const GeoRestricted = React.lazy(() => import('src/pages/geo-restricted/GeoRestricted'));
const FirstTimeUser = React.lazy(() => import('src/pages/login/totp/FirstTimeUser'));
const Verify = React.lazy(() =>
  import('src/pages/verify/Verify').then((m) => ({ default: m.Verify })),
);
const Login = React.lazy(() => import('src/pages/login/Login').then((m) => ({ default: m.Login })));
const ForgotPassword = React.lazy(() =>
  import('src/pages/forgot-password/ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
);
const SessionExpired = React.lazy(() => import('src/pages/session-expired/SessionExpired'));
const AccountExpired = React.lazy(() => import('src/pages/account-expired/AccountExpired'));
const WrongUserStatus = React.lazy(() => import('src/pages/wrong-user-status/WrongUserStatus'));
const FreeSignUp = React.lazy(() => import('src/pages/free-sign-up/FreeSignUp'));
const SessionTestPage = React.lazy(() =>
  import('src/pages/SessionTestPage').then((m) => ({ default: m.SessionTestPage })),
);

// Authenticated pages
const Dashboard = React.lazy(() => import('src/pages/visionx/Dashboard'));
const IntegrationStatus = React.lazy(() => import('src/pages/visionx/IntegrationStatus'));
const NoPhishRedirect = React.lazy(() => import('src/pages/visionx/NoPhishRedirect'));
const NoPhishDashboard = React.lazy(() => import('src/pages/visionx/NoPhishDashboard'));
const NoPhishList = React.lazy(() => import('src/pages/visionx/NoPhishList'));
const NoPhishLegacyFilterRedirect = React.lazy(
  () => import('src/pages/visionx/NoPhishLegacyFilterRedirect'),
);
const SingleNoPhishIncident = React.lazy(
  () => import('src/pages/visionx/SingleNoPhishIncident.tsx'),
);
const SingleIncident = React.lazy(() => import('src/pages/visionx/SingleIncident'));
const ProfileSettings = React.lazy(() => import('src/pages/visionx/ProfileSettings'));
const FeedbackPage = React.lazy(() => import('src/pages/visionx/FeedbackPage'));
const DashboardReports = React.lazy(() => import('src/pages/visionx/DashboardReports'));
const HiddenLogSources = React.lazy(() => import('src/pages/visionx/HiddenLogSources'));
const HiddenUiKit = React.lazy(() => import('src/pages/visionx/HiddenUiKit'));
const GetVisibility = React.lazy(() => import('src/pages/visionx/GetVisibility'));
const Notifications = React.lazy(() => import('src/pages/visionx/Notifications'));
const NoteTracker = React.lazy(() => import('src/pages/visionx/NoteTracker'));
const SplunkStatus = React.lazy(() => import('src/pages/visionx/SplunkStatus'));
const SentinelStatus = React.lazy(() => import('src/pages/visionx/SentinelStatus'));
const RiskHubOverview = React.lazy(() => import('src/pages/visionx/RiskHubOverview'));
const ThreatReports = React.lazy(() => import('src/pages/visionx/ThreatReports'));
const SplashOverview = React.lazy(() => import('src/pages/free-sign-up/SplashOverview'));

// Components used as route elements
const IncidentsListView = React.lazy(
  () => import('src/components/visionx/incidents/IncidentsListView'),
);
const IncidentsChartsView = React.lazy(
  () => import('src/components/visionx/incidents/IncidentsChartsView'),
);
const OpenJiraTicket = React.lazy(
  () => import('src/components/visionx/incidents/open-jira-ticket/OpenJiraTicket'),
);
const Survey = React.lazy(() => import('src/components/visionx/selfAssessment/Survey'));
const Organisations = React.lazy(() => import('src/pages/visionx/super-admin/Organisations'));
const Tenant = React.lazy(() => import('src/pages/visionx/super-admin/Tenant'));
const Recommendations = React.lazy(
  () => import('src/components/visionx/ciso/recommendations/Recommendations'),
);
const Training = React.lazy(() => import('src/components/visionx/ciso/training/Training'));
const AllTenantsListView = React.lazy(
  () => import('src/components/visionx/super-admin/tenants/AllTennatsListView'),
);
const PolicyIntegrations = React.lazy(
  () => import('src/components/visionx/risk-hub/PolicyIntegrations'),
);
const RiskRegister = React.lazy(
  () => import('src/components/visionx/risk-hub/risk-register/RiskRegister'),
);
const SingleQRadarLogSource = React.lazy(
  () => import('src/components/visionx/integration-status/SingleQRadarLogSource'),
);
const SingleSplunkLogSource = React.lazy(
  () => import('src/components/visionx/integration-status/SingleSplunkLogSource'),
);
const SingleSentinelSource = React.lazy(() => import('src/pages/visionx/SingleSentinelSource'));
const SentinelStatusListView = React.lazy(
  () => import('src/components/visionx/integration-status/table/SentinelStatusListView'),
);
const SingleIndustrysegment = React.lazy(
  () => import('src/components/visionx/super-admin/tenants/SingleIndustrysegment'),
);
const RecommendationNewPreview = React.lazy(
  () => import('src/components/visionx/super-admin/recommendations/RecommendationNewPreview'),
);
const RiskDetails = React.lazy(
  () => import('src/components/visionx/risk-hub/risk-register/RiskDetails'),
);
const NoPhishDemo = React.lazy(() => import('src/components/visionx/noPhish/demo/NoPhishDemo'));
const DefenderDashboard = React.lazy(
  () => import('src/components/visionx/defender/DefenderDashboard'),
);
const SentinelOneDashboard = React.lazy(
  () => import('src/components/visionx/sentinelone/SentinelOneDashboard'),
);
const NormalizeSelfAssessmentData = React.lazy(() =>
  import('src/normalizers/NormalizeSelfAssessmentData').then((m) => ({
    default: m.NormalizeSelfAssessmentData,
  })),
);
const SplunkPremium = React.lazy(() =>
  import('src/components/visionx/splunk/SplunkPremium').then((m) => ({ default: m.SplunkPremium })),
);
const SplunkStatusListView = React.lazy(
  () => import('src/components/visionx/integration-status/table/SplunkStatusListView'),
);
const SecopsOverview = React.lazy(() => import('src/components/visionx/chronicle/SecopsOverview'));
const Wizard = React.lazy(
  () => import('src/components/visionx/selfAssessment/start-here/scoping-wizard/Wizard'),
);
const Reporting = React.lazy(() =>
  import('src/components/visionx/selfAssessment/start-here/reporting-wizard/Reporting').then(
    (m) => ({ default: m.Reporting }),
  ),
);
const FullFlowWizard = React.lazy(
  () => import('src/components/visionx/selfAssessment/start-here/full-flow-wizard/FullFlowWizard'),
);
const RiskConfig = React.lazy(
  () => import('src/components/visionx/risk-hub/risk-register/risk-config/RiskConfig'),
);
const ApprovalsPage = React.lazy(() => import('src/components/visionx/approvals/ApprovalsPage'));
const ContentFeedPage = React.lazy(
  () => import('src/components/visionx/content-feed/ContentFeedPage'),
);
const ContentFeedDetail = React.lazy(
  () => import('src/components/visionx/content-feed/ContentFeedDetail'),
);
const ContentFeedAuthorPage = React.lazy(
  () => import('src/components/visionx/content-feed/ContentFeedAuthorPage'),
);
const SecretsManagerPage = React.lazy(
  () => import('src/components/visionx/secrets-manager/SecretsManagerPage'),
);

const DEFAULT_PATH = '/';

export const VISIONX_CONSTANTS: { [id: string]: string } = {
  BASE_PATH: DEFAULT_PATH,
  LOGIN: 'login',
  LOGIN_PATH: DEFAULT_PATH + 'login',
  NOT_FOUND: 'notfound',
  NOT_FOUND_PATH: DEFAULT_PATH + 'notfound',
  VERIFY: 'verify',
  VERIFY_PATH: DEFAULT_PATH + 'verify',
  SIGN_UP_PATH: DEFAULT_PATH + 'sign-up',
  TOTP: 'totp-setup',
  TOTP_PATH: DEFAULT_PATH + 'totp-setup',
  GEO_RESTRICTED: 'geo-restricted',
  GEO_RESTRICTED_PATH: DEFAULT_PATH + 'geo-restricted',
  SESSION_EXPIRED_PATH: DEFAULT_PATH + 'session-expired',
  ACCOUNT_EXPIRED_PATH: DEFAULT_PATH + 'account-expired',
  WRONG_STATUS_PATH: DEFAULT_PATH + 'wrong-status',
  FORGOT_PASSWORD_PATH: DEFAULT_PATH + 'forgot-password',
};

export const PRE_AUTH_PAGES_MAP: { [id: string]: RouteObject[] } = {
  'session-test': [
    {
      path: '/session-test',
      element: <SessionTestPage />,
    },
  ],
  login: [
    {
      path: VISIONX_CONSTANTS.LOGIN_PATH,
      element: <Login />,
    },
  ],
  'sign-up': [
    {
      path: VISIONX_CONSTANTS.SIGN_UP_PATH,
      element: <FreeSignUp />,
    },
  ],
  verify: [
    {
      path: VISIONX_CONSTANTS.VERIFY_PATH + '/:code',
      element: <Verify />,
    },
  ],
  totp: [
    {
      path: VISIONX_CONSTANTS.TOTP_PATH,
      element: <FirstTimeUser />,
    },
  ],
  'geo-restricted': [
    {
      path: VISIONX_CONSTANTS.GEO_RESTRICTED_PATH,
      element: <GeoRestricted />,
    },
  ],
  notfound: [
    {
      path: VISIONX_CONSTANTS.NOT_FOUND_PATH,
      element: <NotFound />,
    },
  ],
  'account-expired': [
    {
      path: VISIONX_CONSTANTS.ACCOUNT_EXPIRED_PATH,
      element: <AccountExpired />,
    },
  ],
  'wrong-status': [
    {
      path: VISIONX_CONSTANTS.WRONG_STATUS_PATH,
      element: <WrongUserStatus />,
    },
  ],
  'forgot-password': [
    {
      path: VISIONX_CONSTANTS.FORGOT_PASSWORD_PATH + '/:code',
      element: <ForgotPassword />,
    },
  ],
  'session-expired': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'session-expired/',
      element: <SessionExpired />,
    },
  ],
};

export const BESPOKE_PAGES_MAP: { [id: string]: RouteObject[] } = {
  loading: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + '/loading',
      element: <LoadingPage />,
    },
  ],
  dashboard: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'dashboard/',
      element: <Dashboard />,
    },
  ],

  'incidents/list': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'incidents/list/',
      element: (
        <>
          <VXErrorBoundary component={<IncidentsListView />} /> <TranscriptDisplay />
        </>
      ),
    },
  ],
  incidents: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'incidents/',
      element: <IncidentsChartsView />,
    },
  ],
  'incidents/new': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'incidents/open-new/',
      element: <OpenJiraTicket issue={'openNew'} />,
    },
  ],

  nophish: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'nophish/',
      element: <NoPhishRedirect />,
    },
  ],
  'nophish/dashboard': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'nophish/dashboard/',
      element: <NoPhishDashboard />,
    },
  ],
  'nophish/list': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'nophish/list/',
      element: <NoPhishList />,
    },
  ],
  'nophish/list/filtered': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'nophish/list/:filter/',
      element: <NoPhishList />,
    },
  ],
  nophishDemo: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'nophish/demo/',
      element: <NoPhishDemo />,
    },
  ],
  'nophish/filtered': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'nophish/:filter/',
      element: <NoPhishLegacyFilterRedirect />,
    },
  ],
  'nophish-single': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'single-nophish/:noPhishId/',
      element: <SingleNoPhishIncident />,
    },
  ],
  'log-sources': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'log-sources/',
      element: <IntegrationStatus />,
    },
  ],
  'log-source': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'log-source/:logSourceId',
      element: <SingleQRadarLogSource />,
    },
  ],
  'hidden-log-sources': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'hidden-log-sources/',
      element: <HiddenLogSources />,
    },
  ],
  'hidden-ui-kit': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'hidden-ui-kit/',
      element: <HiddenUiKit />,
    },
  ],
  'integrations-status': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'integration-status/',
      element: <IntegrationStatus />,
    },
  ],
  splunk: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'splunk/',
      element: <SplunkStatus />,
    },
  ],
  sentinel: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'sentinel/',
      element: <SentinelStatus />,
    },
  ],
  'single-splunk': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'single-splunk/:critical/:logSourceId',
      element: <SingleSplunkLogSource />,
    },
  ],
  'single-sentinel': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'single-sentinel/:dimensionKey',
      element: <SingleSentinelSource />,
    },
  ],
  '/splunk/premium/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'splunk-premium/',
      element: <SplunkPremium />,
    },
  ],

  incident: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + `incident/:incidentId/`,
      element: <SingleIncident />,
    },
  ],
  'incident-with-tab': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + `incident/:incidentId/:openTab`,
      element: <SingleIncident />,
    },
  ],
  'request-help': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'request-help/',
      element: <OpenJiraTicket issue={'requestHelp'} />,
    },
  ],
  'profile-settings': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'profile-settings/',
      element: <ProfileSettings />,
    },
  ],

  feedback: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'feedback/',
      element: <FeedbackPage />,
    },
  ],
  'documents/file-library': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'documents/file-library/',
      element: <DashboardReports />,
    },
  ],
  'documents/note-tracker': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'documents/note-tracker/',
      element: <NoteTracker />,
    },
  ],
  organisations: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'organizations/',
      element: <Organisations />,
    },
  ],
  allTenants: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'organizations/',
      element: <AllTenantsListView />,
    },
  ],
  tenant: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'organization/:organisationId/tenant/:tenantId',
      element: <Tenant />,
    },
  ],
  'tenant-empty': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'organization/:organisationId',
      element: <Tenant />,
    },
  ],
  industrySegment: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'industrysegment',
      element: <SingleIndustrysegment isEdit={false} />,
    },
  ],
  'edit-industrySegment': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'industrysegment/edit/:id',
      element: <SingleIndustrysegment isEdit={true} />,
    },
  ],
  'copy-industrySegment': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'industrysegment/copy/:id',
      element: <SingleIndustrysegment isEdit={false} />,
    },
  ],
  'tenant-tabs': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'organization/:organisationId/tenant/:tenantId/:tab',
      element: <Tenant />,
    },
  ],
  'google-secops/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'google-secops/',
      element: <SecopsOverview />,
    },
  ],
  'hidden-secops-sources': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'hidden-secops-sources',
      element: <SecopsOverview />,
    },
  ],
  'risk-hub/recommendations/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/recommendations/',
      element: <Recommendations />,
    },
  ],
  'risk-hub/recommendation/details/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/recommendation/details/:recommendationId',
      element: <RecommendationNewPreview />,
    },
  ],
  'risk-hub/recommendations/:tenantId': [
    {
      path:
        VISIONX_CONSTANTS.BASE_PATH +
        'risk-hub/recommendations/organization/:organisationId/tenant/:tenantId',
      element: <Recommendations />,
    },
  ],

  'forcepoint/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'forcepoint/',
      element: <GetVisibility />,
    },
  ],
  'defender/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'defender/',
      element: <DefenderDashboard />,
    },
  ],
  'mdr-lite/sentinelone/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'mdr-lite/sentinelone/',
      element: <SentinelOneDashboard />,
    },
  ],
  'risk-hub/overview/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/overview/',
      element: <RiskHubOverview />,
    },
  ],
  'risk-hub/maturity-assessments/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/maturity-assessments/',
      element: <NormalizeSelfAssessmentData />,
    },
  ],
  'risk-hub/maturity-assessments/NIST-survey': [
    {
      path:
        VISIONX_CONSTANTS.BASE_PATH +
        `risk-hub/maturity-assessments/survey/:surveyTitle/:surveyId/:block`,
      element: <Survey />,
    },
  ],
  'risk-hub/risk-register/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/risk-register/',
      element: <RiskRegister />,
    },
  ],
  'risk-hub/risk-register/single-tab': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-register/details/:tabMode/:id',
      element: <RiskDetails />,
    },
  ],
  'risk-hub/risk-register/single-tab/create': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-register/details/:tabMode/',
      element: <RiskDetails />,
    },
  ],
  'risk-register/settings': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-register/settings/',
      element: <RiskConfig />,
    },
  ],
  'risk-hub/policy-integrations/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/policy-integrations/',
      element: <PolicyIntegrations />,
    },
  ],
  'risk-hub/training/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/training/',
      element: <Training />,
    },
  ],
  'notifications/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'notifications/',
      element: <Notifications />,
    },
  ],
  'hidden-splunk-sources': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'hidden-splunk-sources',
      element: <SplunkStatusListView />,
    },
  ],
  'hidden-sentinel-sources': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'hidden-sentinel-sources',
      element: <SentinelStatusListView />,
    },
  ],
  'nis2-wizard': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/maturity-assessments/start-here',
      element: <Wizard />,
    },
  ],
  'nis2-reporting-wizard': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/maturity-assessments/nis2-reporting',
      element: <Reporting />,
    },
  ],
  'nis2-full-flow-wizard': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'risk-hub/maturity-assessments/full-flow-wizard',
      element: <FullFlowWizard />,
    },
  ],
  splash: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'splash',
      element: <SplashOverview />,
    },
  ],
  approvals: [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'approvals/',
      element: <ApprovalsPage />,
    },
  ],
  'threat-intel-reports': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'threat-intel-reports/',
      element: <ContentFeedPage type="THREAT_INTEL" />,
    },
  ],
  'blogs-and-insights': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'blogs-and-insights/',
      element: <ContentFeedPage type="BLOG" />,
    },
  ],
  'content-feed-detail': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'content-feed/:id',
      element: <ContentFeedDetail />,
    },
  ],
  'content-feed-author': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'author/:slug',
      element: <ContentFeedAuthorPage />,
    },
  ],
  'cyber-alerts/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'cyber-alerts/',
      element: <ThreatReports />,
    },
  ],
  'secrets-manager/': [
    {
      path: VISIONX_CONSTANTS.BASE_PATH + 'secrets-manager/',
      element: <SecretsManagerPage />,
    },
  ],
};
